<template>
<div class="box simple">
  Choose Portfolio
  <TypeaheadPortfolioSearch
    shareownersAndPortfolios
    :selected="portfolio"
    :callback="selectPortfolio"
  />
  <br>
  <br>
  <div class="columns">
    <div class="column">
      <label class="label is-medium">
        Alert Rule
      </label>
    </div>
    <div class="column">

    </div>
  </div>
  <div class="columns ">
    <div class="column is-half">
      <p class="help">
        Choose the type of information you want to monitor, then select a metric, direction, and threshold.
      </p>
    </div>
    <div class="column is-half is-right">
      <div class="field is-horizontal is-right">
        <div class="field-label is-small">
          <label class="label">Type of Threshold</label>
        </div>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <b-select v-model="thresholdType" @input="selectThresholdType">
                <option value="n">
                  Number
                </option>
                <option value="p">
                  Percentile
                </option>
                <option value="b">
                  Benchmark Comparison
                </option>
              </b-select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <p class="title is-7">Trigger an alert when:</p>

  <div class="columns">
    <div class="column is-7">
      <div class="field">
        <label class="label">Metric</label>
        <TypeaheadMetricSearch
          :callback="selectMetric"
          :originalMetric="metric"
        />
      </div>
    </div>
    <div class="column">
      <div class="field">
        <label class="label">Condition</label>
        <div class="control">
          <b-select v-model="condition">
            <option value="gt">
              Greater Than
            </option>
            <option value="lt">
              Less Than
            </option>
          </b-select>
        </div>
      </div>
    </div>
    <div class="column">
      <div class="field">
        <label class="label">Threshold <span v-if="thresholdType === 'n' && metric.id && metric.unit !== 'Number'">({{ metric.unit }})</span></label>
        <div class="control">
          <b-input
            type="number"
            v-model="threshold"
          />
        </div>
      </div>
    </div>
  </div><!-- columns -->
  <div class="columns">
    <div class="column is-7">
      <p class="notification is-white" v-show="metric.id">{{ metric.description }}</p>
    </div>
    <div class="column is-5">
      <p class="notification is-info is-light" v-if="metric.id">

        <span v-if="thresholdType !== 'p' && metric.less_is_more">
          A lower value for this metric is usually considered better, most users will want alerts when it is <em><strong>greater than</strong></em> the threshold.
        </span>

        <span v-if="thresholdType !== 'p' && !metric.less_is_more">
          A higher value for this metric is usually considered better, most users will want alerts when it is <em><strong>less than</strong></em> the threshold.
        </span>

        <span v-if="thresholdType === 'p'">
          A higher percentile is always better, so most users will want alerts when your portfolio's percentile is <em><strong>less than</strong></em> the threshold.
          Choose percentile values between 0 (worst possible) and 100 (best possible). The 50th percentile is the median value across all investments.
        </span>

        <span v-if="thresholdType === 'b'">
          <br /><br />
          Benchmark is YourStake's default benchmark.* A benchmark comparison of 0 means your portfolio is equal to the benchmark.
        </span>

      </p>

    </div>
  </div>
  <div class="buttons is-centered">
    <b-tooltip
      :active="!canSubmit"
      label="You must select a portfolio, a metric, and set a threshold to create an alert."
    >
      <b-button
        :disabled="!canSubmit"
        class="button is-primary"
        @click="createMetricAlert"
        :label="metricAlertId ? 'Update Alert' : 'Create Alert'"
      />
      <b-button
        v-if="metricAlertId"
        class="button is-danger"
        @click="deleteMetricAlert"
        label="Delete Alert"
      />
    </b-tooltip>
  </div>
  <div class="columns">
    <div class="column is-6 is-offset-3 mt-2">
      <p class="help" v-if="thresholdType === 'b'">
        *Funds are benchmarked against a representative index based on their investment style (e.g. "US Large Cap"). Companies are benchmarked against the universe
        of companies with data coverage for that metric.
      </p>
    </div>
  </div>
</div><!-- box -->
</template>

<script>
  import TypeaheadMetricSearch from '@frontend/shared/components/TypeaheadMetricSearch.vue';
  import TypeaheadPortfolioSearch from '@frontend/dash3/js/components/portfolios/TypeaheadPortfolioSearch.vue';
  import { getPortfolio } from '@frontend/api/api';

  export default {
    data() {
      return {
        metric: {},
        metricAlertId: '',
        portfolio: {},
        threshold: '',
        condition: 'gt',
        thresholdType: 'n',
      }
    },
    components: {
      TypeaheadMetricSearch,
      TypeaheadPortfolioSearch,
    },
    props: {
      link: String
    },
    methods: {
      async deleteMetricAlert() {
        if (this.metricAlertId) {
          await fetch(`/api/v1/metric-alerts/${this.metricAlertId}/delete`, {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
              'Content-Type': 'application/json',
            },
          })
          window.location.href = '/advisor-dashboard/metric-alerts';
        }
      },
      selectPortfolio(selectedPortfolio) {
        if (!selectedPortfolio) return;
        this.portfolio = selectedPortfolio;
      },
      async createMetricAlert() {
        const body = JSON.stringify({
          modelportfolio: this.portfolio.id,
          threshold_type: this.thresholdType,
          threshold: this.threshold,
          condition: this.condition,
          metric_description: this.metric.id,
        });
        if (this.metricAlertId) {
          await fetch(`/api/v1/metric-alerts/${this.metricAlertId}/update`, {
            method: 'PUT',
            headers: {
              'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
              'Content-Type': 'application/json',
            },
            body,
          })
          window.location.href = '/advisor-dashboard/metric-alerts';
          return;
        }
        await fetch('/api/v1/metric-alerts/create-metric-alert/', {
          method: 'POST',
          headers: {
            'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]').value,
            'Content-Type': 'application/json',
          },
          body,
        })
        window.location.href = '/advisor-dashboard/metric-alerts';
      },
      selectThresholdType() {
        if (this.thresholdType === 'p') {
          this.condition = 'lt';
        }
        else if (this.metric && this.metric.id) {
          if (this.metric.less_is_more) {
            this.condition = 'gt';
          }
          else {
            this.condition = 'lt';
          }
        }
      },
      selectMetric(metric) {
        if (metric) {
          this.metric = metric;
          if (this.metric.less_is_more && this.thresholdType !== 'p') {
            this.condition = 'gt';
          }
          else {
            this.condition = 'lt';
          }
        }
        else {
          this.metric = {};
        }
      }
    },
    computed: {
      canSubmit() {
        return this.portfolio.id && this.threshold && this.condition && this.thresholdType
      }
    },
    async created() {
      this.metricAlertId = (new URLSearchParams(window.location.search)).get('metric_alert_id');

      if (this.metricAlertId) {
        const metricAlert = (await (await fetch(`/api/v1/metric-alerts/${this.metricAlertId}/`)).json()).metric_alert;
        this.portfolio = { ...metricAlert.modelportfolio, ...metricAlert.shareowner, id: metricAlert.modelportfolio?.external_id || metricAlert.shareowner?.external_id };
        this.thresholdType = metricAlert.threshold_type;
        this.threshold = metricAlert.threshold;
        this.condition = metricAlert.condition;
        this.metric = metricAlert.metric_description;
      }
      const portfolioId = (new URLSearchParams(window.location.search)).get('portfolio');
      if (portfolioId) {
        this.portfolio = (await getPortfolio(portfolioId)).data.portfolio;
      }
    },
  }
</script>

<style lang="scss">

</style>
